import { Button, Center, Group, Stack, Title } from "@mantine/core";
import { Link, NavLink, useLocation } from "@remix-run/react";
import { IconMicroscope, IconSchool } from "@tabler/icons-react";

import ClockFast from "~/assets/icons/clock-fast.svg?react";
import IconDoctor from "~/assets/icons/doctor.svg?react";
import { useTranslation } from "~/hooks/use-typed-translation";

import classes from "./book-healthcare-hero.module.scss";

interface BookHealthCareHeroProps {
  heading?: string;
  partnerSlug?: string;
  headingOrder?: 1 | 2;
}

export const BookHealthCareHero = ({
  heading,
  partnerSlug,
  headingOrder = 2,
}: BookHealthCareHeroProps) => {
  const { i18n, t } = useTranslation();
  const { pathname } = useLocation();

  const splitPathname = pathname.split("/");
  const isDoctorsPage = partnerSlug ? pathname.endsWith("/doctors") :
    !(splitPathname.length > 2) ||
    (splitPathname.length > 2 &&
      splitPathname?.at(-1) === "" &&
      splitPathname?.at(-2) !== "lab");
  const isHealthLibraryPage = splitPathname.includes("health-library");
  // console.log("is doctor page:", isDoctorsPage);

  return (
    <section className={classes.section}>
      <Title
        order={headingOrder}
        fw={600}
        ta={"center"}
        className={classes.heading}>
        {heading ?? t("tailor-made-care")}
      </Title>

      <Center mt="2rem">
        <Stack gap={"3rem"}>
          {!partnerSlug && <Button
            component={Link}
            to={`/${i18n.language}/doctor/asap`}
            reloadDocument
            leftSection={<ClockFast />}
            color="accent"
            radius={"xl"}
            h={45}
            w="fit-content"
            mx="auto">
            {t("doctor-in-30-mins")}
          </Button>}

          <div className={classes["btns-wrapper"]}>
            <NavLink
              className={classes["segment-link"]}
              to={`/${i18n.language}${partnerSlug ? `/partners/${partnerSlug}/doctors` : ""}`}
              reloadDocument>
              {({ isActive }) => (
                <div
                  className={`${classes["segment-control"]} ${
                    isActive && isDoctorsPage // Extra check because "isActive" now every nested route
                      ? classes.active
                      : ""
                  }`}>
                  <Group gap={6} wrap="nowrap">
                    <IconDoctor />
                    <span>{t("specialists")}</span>
                  </Group>
                </div>
              )}
            </NavLink>
            <span className={classes["segment-divider"]} aria-hidden="true">
              |
            </span>
            <NavLink
              className={classes["segment-link"]}
              to={`/${i18n.language}${partnerSlug ? `/partners/${partnerSlug}` : ""}/lab`}
              reloadDocument>
              {({ isActive }) => (
                <div
                  className={`${classes["segment-control"]} ${
                    isActive ? classes.active : ""
                  }`}>
                  <Group gap={6} wrap="nowrap">
                    <IconMicroscope size={24} stroke={1.5} />
                    <span>{t("labs")}</span>
                  </Group>
                </div>
              )}
            </NavLink>
            
            <span className={classes["segment-divider"]} aria-hidden="true">
              |
            </span>
            <NavLink
              className={classes["segment-link"]}
              to={`/${i18n.language}/health-library`}
              reloadDocument>
              {({ isActive }) => (
                <div
                  className={`${classes["segment-control"]} ${
                    isActive || isHealthLibraryPage ? classes.active : ""
                  }`}>
                  <Group gap={6} wrap="nowrap">
                    <IconSchool size={24} stroke={1.5} />
                    <span>{t("health-library")}</span>
                  </Group>
                </div>
              )}
            </NavLink>
          </div>
        </Stack>
      </Center>
    </section>
  );
};
